/**
 * Citadel — Run-Status Reconciliation (Trust Integrity Monitor).
 *
 * Surfaces the BL-29-2170 trust-integrity backstop: every orbiset_runs row
 * that claims SUCCEEDED while at least one child orb_runs row is FAILED.
 * These rows are trust-integrity violations — the signed OPAL receipt would
 * claim the run succeeded while step-level evidence shows failure.
 *
 * A count of zero means the platform is clean. Any non-zero count is a
 * critical signal that the BL-29-2168/2169 engine+receipt fixes have
 * regressed or that a new divergence path has been introduced.
 *
 * Visual identity: terminal aesthetic per Citadel_Architecture_ADR.md.
 * Reads like Datadog/Grafana/Honeycomb alert surfaces.
 *
 * Auth model: reachable only through Cloudflare Access (staff allowlist).
 * The perm("operations:read") gate is enforced by the operon backend.
 * Loading is gated on isApiReady() per the BL-29-2163 pattern.
 *
 * Sprint: SPR-26.O | BL-29-2194 | Role: Frontend Developer
 */
import { useCallback, useEffect, useRef, useState } from 'react';
// Synthesis XII-5: Phosphor (thin) for prominent surface chrome (size-4/size-5),
// Heroicons micro (16/solid) for dense in-row badges (size-3/size-3.5).
import {
    Warning as AlertTriangle,
    CheckCircle as CheckCircle2,
    ShieldWarning as ShieldAlert,
    ShieldCheck,
} from 'phosphor-react';
import {
    ExclamationTriangleIcon as MicroAlertTriangle,
    CheckCircleIcon as MicroCheckCircle,
    ArrowPathIcon as MicroSpinner,
} from '@heroicons/react/16/solid';
import {
    type RunStatusDivergenceRow,
    type RunStatusReconciliationResponse,
    listRunStatusDivergences,
} from '@/lib/api';
import { useAccessTokenSync } from '@/lib/auth';

// ── Types ─────────────────────────────────────────────────────────────────────

type LoadState = 'loading' | 'ready' | 'error';

// ── Helpers ───────────────────────────────────────────────────────────────────

/**
 * Format an ISO 8601 UTC timestamp as a compact human-readable string
 * suitable for a dense ops table (e.g. "2026-05-21 12:34:56").
 * Returns "—" for null/undefined values.
 */
function fmtTs(iso: string | null | undefined): string {
    if (!iso) return '—';
    // Replace the 'T' separator and strip the trailing milliseconds + Z
    // for a compact display: "2026-05-21T12:34:56.000Z" → "2026-05-21 12:34:56"
    return iso.replace('T', ' ').replace(/\.\d{3}Z$/, '').replace('Z', '');
}

// ── Sub-components ────────────────────────────────────────────────────────────

interface DivergenceTableProps {
    divergences: RunStatusDivergenceRow[];
}

/**
 * Dense ops table of trust-integrity violations.
 * Each row: run id · tenant/workspace/target · claimed status · failed child count · timestamps.
 */
function DivergenceTable({ divergences }: DivergenceTableProps) {
    return (
        <div
            className="overflow-x-auto"
            data-testid="divergence-table"
            role="region"
            aria-label="Run-status divergences"
        >
            <table className="w-full text-xs font-mono border-collapse">
                <thead>
                    <tr className="border-b border-citadel-border">
                        <th
                            scope="col"
                            className="py-2 px-3 text-left text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Run ID
                        </th>
                        <th
                            scope="col"
                            className="py-2 px-3 text-left text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Tenant
                        </th>
                        <th
                            scope="col"
                            className="py-2 px-3 text-left text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Workspace
                        </th>
                        <th
                            scope="col"
                            className="py-2 px-3 text-left text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Target
                        </th>
                        <th
                            scope="col"
                            className="py-2 px-3 text-left text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Claimed status
                        </th>
                        <th
                            scope="col"
                            className="py-2 px-3 text-right text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Failed orbs
                        </th>
                        <th
                            scope="col"
                            className="py-2 px-3 text-left text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Started at
                        </th>
                        <th
                            scope="col"
                            className="py-2 px-3 text-left text-2xs font-semibold uppercase tracking-wider text-citadel-ink-faint whitespace-nowrap"
                        >
                            Completed at
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {divergences.map((row) => (
                        <tr
                            key={row.orbiset_run_id}
                            data-testid={`divergence-row-${row.orbiset_run_id}`}
                            className="border-b border-citadel-border/50 hover:bg-citadel-surface-2 transition-colors"
                        >
                            {/* Run ID — truncated for density, full value in title */}
                            <td
                                className="py-2 px-3 text-citadel-ink-dim whitespace-nowrap"
                                title={row.orbiset_run_id}
                            >
                                <span className="text-citadel-danger/80">
                                    {row.orbiset_run_id.length > 16
                                        ? `${row.orbiset_run_id.slice(0, 16)}…`
                                        : row.orbiset_run_id}
                                </span>
                            </td>
                            <td
                                className="py-2 px-3 text-citadel-ink-dim whitespace-nowrap"
                                title={row.tenant_id}
                            >
                                {row.tenant_id.length > 14
                                    ? `${row.tenant_id.slice(0, 14)}…`
                                    : row.tenant_id}
                            </td>
                            <td className="py-2 px-3 text-citadel-ink-dim whitespace-nowrap">
                                {row.workspace_id}
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                                <span
                                    className={`text-2xs font-semibold uppercase tracking-wider px-1.5 py-0.5 ${
                                        row.deployment_target === 'live'
                                            ? 'bg-citadel-cyan/10 text-citadel-cyan'
                                            : 'bg-citadel-amber/10 text-citadel-amber'
                                    }`}
                                >
                                    {row.deployment_target}
                                </span>
                            </td>
                            <td className="py-2 px-3 whitespace-nowrap">
                                {/* Claimed SUCCEEDED on a run that actually failed — red badge */}
                                <span className="text-2xs font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-citadel-danger/10 text-citadel-danger">
                                    {row.orbiset_run_status}
                                </span>
                            </td>
                            <td
                                className="py-2 px-3 text-right text-citadel-danger font-semibold whitespace-nowrap"
                                data-testid={`divergence-failed-count-${row.orbiset_run_id}`}
                            >
                                {row.failed_orb_run_count}
                            </td>
                            <td className="py-2 px-3 text-citadel-ink-faint whitespace-nowrap">
                                {fmtTs(row.started_at)}
                            </td>
                            <td className="py-2 px-3 text-citadel-ink-faint whitespace-nowrap">
                                {fmtTs(row.completed_at)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function RunStatusReconciliation() {
    // BL-29-2163 pattern: gate the initial load on API readiness so the first
    // /v1/citadel/* call never races the async Auth0 token + tenant resolution.
    const { ready } = useAccessTokenSync();

    const [loadState, setLoadState] = useState<LoadState>('loading');
    const [loadError, setLoadError] = useState<string | null>(null);
    const [result, setResult] = useState<RunStatusReconciliationResponse | null>(null);

    const mountedRef = useRef(true);
    useEffect(() => {
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const refresh = useCallback(async () => {
        setLoadState('loading');
        setLoadError(null);
        try {
            const res = await listRunStatusDivergences();
            if (!mountedRef.current) return;
            setResult(res);
            setLoadState('ready');
        } catch (err) {
            if (!mountedRef.current) return;
            setLoadError(
                err instanceof Error
                    ? err.message
                    : 'Failed to load reconciliation data.',
            );
            setLoadState('error');
        }
    }, []);

    // Gate the initial load on API readiness — identical to FeatureSwitches
    // and TemplatesModeration (BL-29-2163).
    useEffect(() => {
        if (!ready) return;
        void refresh();
    }, [ready, refresh]);

    const isClean = result !== null && result.total_count === 0;
    const hasDivergences = result !== null && result.total_count > 0;

    return (
        <section
            className="space-y-4"
            aria-labelledby="run-status-reconciliation-heading"
            data-testid="run-status-reconciliation"
        >
            {/* ── Header ──────────────────────────────────────────────────── */}
            <header className="flex items-start justify-between gap-4">
                <div>
                    <div className="flex items-center gap-2">
                        {hasDivergences ? (
                            <ShieldAlert
                                className="size-4 text-citadel-danger"
                                weight="thin"
                                aria-hidden
                            />
                        ) : (
                            <ShieldCheck
                                className="size-4 text-citadel-cyan"
                                weight="thin"
                                aria-hidden
                            />
                        )}
                        <h1
                            id="run-status-reconciliation-heading"
                            className="text-lg font-semibold tracking-tight"
                        >
                            Run-status reconciliation
                        </h1>
                        {hasDivergences && (
                            <span
                                className="text-2xs font-mono font-semibold uppercase tracking-wider px-1.5 py-0.5 bg-citadel-danger/15 text-citadel-danger border border-citadel-danger/30"
                                data-testid="alert-badge"
                                aria-label={`${result!.total_count} trust-integrity violations`}
                            >
                                {result!.total_count} violation
                                {result!.total_count !== 1 ? 's' : ''}
                            </span>
                        )}
                    </div>
                    <p className="mt-1 text-xs text-citadel-ink-dim leading-relaxed max-w-2xl">
                        Trust-integrity monitor (BL-29-2170). Shows every{' '}
                        <span className="font-mono">orbiset_runs</span> row claiming{' '}
                        <span className="font-mono">SUCCEEDED</span> while at least one
                        child <span className="font-mono">orb_runs</span> row is{' '}
                        <span className="font-mono">FAILED</span>. A count of zero means
                        the platform is clean. Any non-zero count is a P0 signal.
                    </p>
                </div>
                <span className="text-2xs font-mono text-citadel-ink-faint shrink-0 mt-1">
                    /v1/citadel/integrity/run-status-reconciliation
                </span>
            </header>

            {/* ── Loading state ────────────────────────────────────────────── */}
            {loadState === 'loading' && (
                <div
                    role="status"
                    className="flex items-center gap-2 border border-citadel-border bg-citadel-surface p-3 text-xs font-mono text-citadel-ink-dim"
                    data-testid="loading-state"
                >
                    <MicroSpinner className="size-3.5 animate-spin" />
                    Running reconciliation query...
                </div>
            )}

            {/* ── Error state ──────────────────────────────────────────────── */}
            {loadState === 'error' && (
                <div
                    role="alert"
                    className="flex items-start gap-2 border border-citadel-danger/40 bg-citadel-danger/10 p-3"
                    data-testid="error-state"
                >
                    <AlertTriangle className="size-4 text-citadel-danger mt-0.5 shrink-0" weight="thin" />
                    <div className="text-xs">
                        <p className="font-semibold text-citadel-danger">
                            Reconciliation query failed.
                        </p>
                        <p className="mt-1 text-citadel-ink-dim">{loadError}</p>
                        <button
                            type="button"
                            onClick={() => void refresh()}
                            className="mt-2 text-2xs font-mono uppercase tracking-wider px-2 py-1 border border-citadel-danger/40 text-citadel-danger hover:bg-citadel-danger/10"
                        >
                            Retry
                        </button>
                    </div>
                </div>
            )}

            {/* ── Ready state ──────────────────────────────────────────────── */}
            {loadState === 'ready' && result !== null && (
                <>
                    {/* Count summary bar */}
                    <div
                        className={`flex items-center justify-between gap-4 border p-3 text-xs font-mono ${
                            hasDivergences
                                ? 'border-citadel-danger/40 bg-citadel-danger/10'
                                : 'border-citadel-border bg-citadel-surface'
                        }`}
                        data-testid="summary-bar"
                    >
                        <div className="flex items-center gap-2">
                            {hasDivergences ? (
                                <MicroAlertTriangle
                                    className="size-3.5 text-citadel-danger shrink-0"
                                    aria-hidden
                                />
                            ) : (
                                <MicroCheckCircle
                                    className="size-3.5 text-citadel-success shrink-0"
                                    aria-hidden
                                />
                            )}
                            <span
                                className={
                                    hasDivergences
                                        ? 'text-citadel-danger'
                                        : 'text-citadel-success'
                                }
                                data-testid="summary-text"
                            >
                                {isClean
                                    ? 'No divergences — all runs consistent.'
                                    : `${result.total_count} divergence${result.total_count !== 1 ? 's' : ''} detected — trust-integrity violation.`}
                            </span>
                        </div>
                        {/* total_count vs returned_count so operator sees true total even when capped */}
                        <div className="text-citadel-ink-faint text-2xs shrink-0">
                            {result.returned_count < result.total_count ? (
                                <span>
                                    Showing{' '}
                                    <span className="text-citadel-amber">
                                        {result.returned_count}
                                    </span>{' '}
                                    of{' '}
                                    <span className="text-citadel-danger">
                                        {result.total_count}
                                    </span>{' '}
                                    (cap: {result.limit})
                                </span>
                            ) : (
                                <span
                                    data-testid="count-display"
                                >
                                    total: {result.total_count} · returned:{' '}
                                    {result.returned_count} · cap: {result.limit}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* ── Critical alert banner when violations exist ───────── */}
                    {hasDivergences && (
                        <div
                            role="alert"
                            className="border border-citadel-danger/50 bg-citadel-danger/10 p-4"
                            data-testid="critical-alert"
                        >
                            <div className="flex items-start gap-3">
                                <ShieldAlert
                                    className="size-5 text-citadel-danger shrink-0 mt-0.5"
                                    weight="thin"
                                    aria-hidden
                                />
                                <div className="space-y-1">
                                    <p className="text-sm font-semibold text-citadel-danger">
                                        CRITICAL — Trust-integrity violation detected
                                    </p>
                                    <p className="text-xs text-citadel-ink-dim leading-relaxed">
                                        The rows below claim{' '}
                                        <span className="font-mono text-citadel-ink">
                                            SUCCEEDED
                                        </span>{' '}
                                        while a child orb run is{' '}
                                        <span className="font-mono text-citadel-ink">
                                            FAILED
                                        </span>
                                        . The signed OPAL receipts for these runs are
                                        self-contradicting. Investigate immediately via
                                        the BL-29-2165 Fail-Loud Doctrine checklist.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Divergence table (only when there are rows) ──────── */}
                    {hasDivergences && (
                        <div className="border border-citadel-border bg-citadel-surface">
                            <DivergenceTable divergences={result.divergences} />
                        </div>
                    )}

                    {/* ── Healthy state (zero violations) ─────────────────── */}
                    {isClean && (
                        <div
                            className="flex items-center gap-2 border border-citadel-success/20 bg-citadel-success/5 p-3"
                            data-testid="healthy-state"
                        >
                            <CheckCircle2
                                className="size-4 text-citadel-success shrink-0"
                                weight="thin"
                                aria-hidden
                            />
                            <p className="text-xs text-citadel-ink-dim">
                                Platform integrity confirmed. No orbiset runs claim
                                success while a child orb run is failed.
                            </p>
                        </div>
                    )}
                </>
            )}

            {/* ── Footer: refresh button (available once loaded or errored) ── */}
            {loadState !== 'loading' && (
                <div className="flex items-center justify-end pt-1">
                    <button
                        type="button"
                        onClick={() => void refresh()}
                        className="text-2xs font-mono uppercase tracking-wider px-3 py-1.5 border border-citadel-border text-citadel-ink-dim hover:border-citadel-border-strong hover:text-citadel-ink"
                        data-testid="refresh-button"
                    >
                        Refresh
                    </button>
                </div>
            )}
        </section>
    );
}
